import { Link } from "react-router-dom";
import { LIBRARY_IMAGE } from "@/lib/content";
import SEO from "@/components/SEO";
import PracticeSignature from "@/components/PracticeSignature";
import PracticeByline from "@/components/PracticeByline";
import { breadcrumbSchema, faqPageSchema, authoredPracticePageSchema } from "@/lib/schemas";
import { ArrowRight } from "lucide-react";

const TRADEMARK_PROTECT = [
  "Business, firm, and agency names",
  "Product lines, merchandise, and private-label brands",
  "Creator, influencer, and professional names",
  "Social-media account and channel names",
  "Podcast, blog, and newsletter names",
  "Logos, slogans, and taglines",
  "Course, coaching-program, and membership names",
  "Event, conference, and community names",
  "Mobile applications and digital-platform brands",
];

const TRADEMARK_SERVICES = [
  "Preliminary trademark availability reviews",
  "Comprehensive trademark clearance searches",
  "USPTO federal trademark applications",
  "Texas and other state trademark applications",
  "Responses to USPTO Office Actions",
  "Statements of Use and extension requests",
  "Trademark renewals and maintenance filings",
  "Trademark assignments and ownership transfers",
  "Trademark licensing agreements",
  "Co-branding and collaboration agreements",
  "Monitoring for confusingly similar brands",
  "Cease-and-desist letters",
  "Opposition, cancellation, and infringement strategy",
  "Online impersonation and counterfeit-brand issues",
];

const COPYRIGHT_PROTECT = [
  "Books, e-books, downloadable guides",
  "Blog posts, newsletters, and written articles",
  "Videos, reels, and livestream recordings",
  "Photographs and digital images",
  "Podcasts and recorded interviews",
  "Online courses and educational materials",
  "Webinars, presentations, and training programs",
  "Graphic designs, artwork, and illustrations",
  "Website text and original website content",
  "Music, sound recordings, and audiovisual works",
  "Software, applications, and certain digital content",
  "Advertising and promotional materials",
];

const COPYRIGHT_SERVICES = [
  "Copyright ownership reviews",
  "Registration with the U.S. Copyright Office",
  "Registration strategies for works and eligible groups",
  "Work-made-for-hire agreements",
  "Copyright assignments and transfers",
  "Photographer, videographer, and editor agreements",
  "Content-development agreements",
  "Copyright licensing and royalty agreements",
  "Permission and release forms",
  "DMCA takedown assistance",
  "Cease-and-desist and infringement-demand letters",
  "Copyright policies for websites and platforms",
  "AI-generated content and ownership considerations",
];

const LICENSING = [
  "Brand licensing", "Merchandise licensing", "Content syndication",
  "Course and curriculum licensing", "Podcast and media licensing",
  "Name, image, and likeness agreements", "Affiliate and endorsement agreements",
  "Royalty and revenue-sharing arrangements", "Franchise and brand-expansion planning",
  "Intellectual-property holding companies", "Sale or transfer of IP-owned brands",
];

const CROSS_LINKS = [
  {
    from: "Business Counsel",
    body: "Entity structures with proper IP holding — separate operating and IP companies to isolate liability and enable clean licensing between them.",
    href: "/practice",
    testId: "ip-cross-business",
  },
  {
    from: "Creator & Influencer Counsel",
    body: "Portfolio-build of federal registrations for a creator's name, channel, catchphrases, merchandise, and course brands — the essential asset protection before the next brand deal.",
    href: "/practice/creators-and-athletes",
    testId: "ip-cross-creators",
  },
  {
    from: "Trust & Estate Planning",
    body: "Trademarks and copyrights are assets that pass at death. Proper trust drafting, assignment mechanics, and successor-owner designations preserve IP value across generations.",
    href: "/practice",
    testId: "ip-cross-estate",
  },
  {
    from: "Business Succession",
    body: "When a business changes hands, its trademarks and copyrights must be properly assigned. Documented IP ownership is often the single largest gap in unprepared succession transactions.",
    href: "/practice",
    testId: "ip-cross-succession",
  },
];

const IP_FAQ = [
  {
    q: "Do I need a federal trademark, or is state registration enough?",
    a: "Federal registration through the USPTO provides nationwide protection, public notice of ownership, and enforcement benefits state registration cannot match. State registration is a supplementary layer within a particular state. For any business, creator, or professional practice operating beyond a single state — or building brand value likely to expand — federal registration is strongly preferred.",
  },
  {
    q: "Does copyright require registration if it arises automatically?",
    a: "Copyright arises automatically the moment an original work is fixed in tangible form. Registration is not required for the copyright to exist, but registration is required to bring a federal infringement lawsuit, to recover statutory damages and attorney fees, and to record with U.S. Customs. For any work of commercial value, registration is essential enforcement infrastructure — not optional.",
  },
  {
    q: "Who owns intellectual property created by employees or contractors?",
    a: "Under U.S. copyright law, work created by an employee within the scope of employment is owned by the employer as 'work made for hire.' Work created by an independent contractor is typically owned by the contractor unless a written work-made-for-hire agreement transfers ownership. Trademark ownership follows a different analysis — typically vested in the party using the mark commercially. Both scenarios require careful contractual documentation to avoid disputes.",
  },
  {
    q: "How does AI-generated content affect ownership?",
    a: "The U.S. Copyright Office currently takes the position that purely AI-generated content is not copyrightable, but human-authored works incorporating AI-assisted elements may be — depending on the extent of human creative input. This is an evolving area of law. We advise creators, agencies, and businesses on the practical drafting and documentation strategies that protect ownership claims in mixed human/AI content.",
  },
];

export default function IntellectualProperty() {
  return (
    <div className="bg-ivory" data-testid="ip-page">
      <SEO
        title="Intellectual Property — Trademarks, Copyrights & Brand Protection · Lone Star Counsel"
        description="Texas trademark and copyright counsel for business owners, creators, professional practices, and families. USPTO filings, licensing, enforcement, and estate coordination — all from one desk."
        path="/practice/intellectual-property"
        schema={[
          ...authoredPracticePageSchema({
            path: "/practice/intellectual-property",
            title: "Intellectual Property — Trademarks, Copyrights & Brand Protection · Lone Star Counsel",
            description: "Texas trademark and copyright counsel for business owners, creators, professional practices, and families. USPTO filings, licensing, enforcement, and estate coordination — all from one desk.",
            discipline: "Intellectual Property",
            minutes: 7,
            lastReviewed: "2026-02-01",
            publishedDate: "2024-09-01",
          }),
          faqPageSchema(IP_FAQ),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/practice" },
            { name: "Intellectual Property", path: "/practice/intellectual-property" },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="relative panel-navy min-h-[70vh] flex items-end" data-testid="ip-hero">
        <div className="absolute inset-0">
          <img src={LIBRARY_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/55 to-navy/95" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <div className="kicker kicker-ivory">Intellectual Property</div>
          <div className="mt-3 text-[11px] uppercase tracking-[0.42em] text-brass font-medium">
            Trademarks <span className="text-brass/50 mx-2">·</span>
            Copyrights <span className="text-brass/50 mx-2">·</span>
            Brand Protection
          </div>
          <h1 className="mt-8 font-display text-ivory text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
            The name is yours. <br/>
            <span className="italic font-cormorant text-brass">Prove it.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-cormorant italic text-2xl md:text-3xl text-ivory/85 leading-snug">
            Federal trademarks, copyright registrations, and the licensing architecture that
            converts brand equity into a durable, defensible business asset.
          </p>
        </div>
      </section>

      <PracticeByline
        minutes={7}
        lastReviewed="February 2026"
        discipline="Intellectual Property"
      />

      <PracticeSignature
        src="/img/practice-intellectual-property.jpg"
        kicker="A Signature Detail"
        caption="A drawing compass, a brass magnifier, an ink pot and a registration seal — the quiet apparatus of a protected idea."
      />

      {/* INTRO */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="ip-intro">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kicker">The Discipline</div>
            <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.0]">
              An asset, not<br/>
              <span className="italic font-cormorant text-oxblood">an afterthought.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-lg md:text-xl font-light text-slate2 leading-relaxed space-y-6">
            <p className="dropcap">
              For business owners, creators, professional practices, and families with brand
              equity worth protecting, intellectual property is often the most valuable —
              and most under-protected — asset on the balance sheet. Lone Star Counsel helps
              clients identify, register, license, and enforce the trademarks, copyrights, and
              related IP that anchor a durable enterprise.
            </p>
            <p className="font-cormorant italic text-brass text-xl leading-snug pt-4 hairline-top">
              Your reputation earns the attention. Your intellectual property secures the value.
            </p>
          </div>
        </div>
      </section>

      {/* TRADEMARK */}
      <section className="bg-linen py-24 md:py-32" data-testid="ip-trademark">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14">
            <div className="kicker">01 · Trademark &amp; Brand Protection</div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
              Names, logos,<br/>
              <span className="italic font-cormorant text-oxblood">and everything that identifies you.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">We protect</div>
              <ul className="mt-6 space-y-2 text-base md:text-lg font-light text-slate2 leading-snug">
                {TRADEMARK_PROTECT.map((it) => (
                  <li key={it} className="flex items-baseline gap-3"><span className="text-brass mt-1 text-[0.55rem]">◆</span><span>{it}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Trademark services</div>
              <ul className="mt-6 space-y-2 text-base md:text-lg font-light text-slate2 leading-snug">
                {TRADEMARK_SERVICES.map((it) => (
                  <li key={it} className="flex items-baseline gap-3"><span className="text-brass mt-1 text-[0.55rem]">◆</span><span>{it}</span></li>
                ))}
              </ul>
            </div>
          </div>
          <p className="mt-12 font-cormorant italic text-slate2/85 text-lg md:text-xl leading-snug max-w-3xl">
            Federal registration through the USPTO provides nationwide benefits and public notice
            of the owner&apos;s claim. State registration provides an additional or more limited
            layer of protection within a particular state.
          </p>
        </div>
      </section>

      {/* COPYRIGHT */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="ip-copyright">
        <div className="max-w-3xl mb-14">
          <div className="kicker">02 · Copyright Protection</div>
          <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
            Original works,<br/>
            <span className="italic font-cormorant text-oxblood">properly claimed.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Works we help protect</div>
            <ul className="mt-6 space-y-2 text-base md:text-lg font-light text-slate2 leading-snug">
              {COPYRIGHT_PROTECT.map((it) => (
                <li key={it} className="flex items-baseline gap-3"><span className="text-brass mt-1 text-[0.55rem]">◆</span><span>{it}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Copyright services</div>
            <ul className="mt-6 space-y-2 text-base md:text-lg font-light text-slate2 leading-snug">
              {COPYRIGHT_SERVICES.map((it) => (
                <li key={it} className="flex items-baseline gap-3"><span className="text-brass mt-1 text-[0.55rem]">◆</span><span>{it}</span></li>
              ))}
            </ul>
          </div>
        </div>
        <p className="mt-12 font-cormorant italic text-slate2/85 text-lg md:text-xl leading-snug max-w-3xl">
          Copyright generally arises automatically when an original work is created and fixed in
          a tangible form. Registration with the U.S. Copyright Office, however, provides
          important additional enforcement and litigation benefits.
        </p>
      </section>

      {/* LICENSING */}
      <section className="panel-navy py-24 md:py-32" data-testid="ip-licensing">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="kicker kicker-ivory">03 · Licensing &amp; Monetization</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl text-ivory leading-[1.02]">
                Turning IP into<br/>
                <span className="italic font-cormorant text-brass">recurring business value.</span>
              </h2>
            </div>
            <ul className="lg:col-span-6 lg:col-start-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              {LICENSING.map((it, i) => (
                <li key={it} className="hairline-top pt-5 font-cormorant text-lg md:text-xl text-ivory italic leading-snug" data-testid={`ip-lic-${i}`}>
                  <span className="text-brass font-sans not-italic text-xs mr-3 font-medium">{String(i + 1).padStart(2, "0")}</span>
                  {it}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CROSS-LINKS */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="ip-cross-links">
        <div className="max-w-3xl mb-14">
          <div className="kicker">Where IP Intersects</div>
          <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
            The disciplines<br/>
            <span className="italic font-cormorant text-oxblood">it touches.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
          {CROSS_LINKS.map((c) => (
            <Link
              key={c.from}
              to={c.href}
              className="group hairline-top pt-8 block"
              data-testid={c.testId}
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">{c.from}</div>
              <p className="mt-4 text-base md:text-lg font-light text-slate2 leading-relaxed">{c.body}</p>
              <div className="mt-5 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-oxblood group-hover:text-navy transition font-medium">
                Explore {c.from} <ArrowRight size={13} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-linen py-24 md:py-32" data-testid="ip-faq-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="mb-14">
            <div className="kicker">Frequently Asked</div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
              IP questions,<br/>
              <span className="italic font-cormorant text-oxblood">plainly answered.</span>
            </h2>
          </div>
          <div className="space-y-10">
            {IP_FAQ.map((f, i) => (
              <article key={i} className="hairline-top pt-8" data-testid={`ip-faq-${i}`}>
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Question {String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-4 font-display text-2xl md:text-3xl text-navy leading-[1.15]">{f.q}</h3>
                <p className="mt-5 text-base md:text-lg font-light text-slate2 leading-relaxed">{f.a}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-24 text-center" data-testid="ip-cta">
        <div className="kicker">Begin</div>
        <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
          Ready to protect<br/>
          <span className="italic font-cormorant text-oxblood">what you have built?</span>
        </h2>
        <p className="mt-8 max-w-2xl mx-auto font-cormorant italic text-lg md:text-xl text-slate2 leading-snug">
          A private conversation begins with a preliminary review of your existing marks, works,
          and portfolio gaps. Complimentary for the first hour.
        </p>
        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <Link to="/intake" className="btn-primary" data-testid="ip-intake-cta">
            Request Conference <ArrowRight size={16} />
          </Link>
          <Link to="/journal/register-your-trademark-before-your-next-brand-deal" className="btn-ghost" data-testid="ip-journal-cta">
            Read the Trademark Essay
          </Link>
        </div>
      </section>
    </div>
  );
}
